///
/// EarthBrowser
///
/// Created by Matt Giger on 12/18/2025
/// Copyright (c) 2025 Geodata Labs
///

import type { IDataProvider } from '@/data-providers/types'
import { setPagination } from '@/data-providers/queryBuilder'

export interface ExportToGeoPackageOptions {
	provider: IDataProvider
	outputName: string
	description?: string
	maxRows?: number
}

/**
 * Export data from a provider to GeoPackage format
 *
 * @param options Export configuration
 * @returns Promise resolving to true on success
 */
export async function exportToGeoPackage(options: ExportToGeoPackageOptions): Promise<boolean> {
	const {
		provider,
		outputName,
		description = `Exported from ${outputName}`,
		maxRows = 100000
	} = options

	try {
		// Get all data (up to maxRows)
		const query = setPagination({}, 0, maxRows)
		const result = await provider.query(query)

		if (result.rows.length === 0) {
			console.warn('No data to export')
			return false
		}

		// Convert DataRow[] to Record<string, unknown>[]
		const csvData = result.rows.map(row => {
			const record: Record<string, unknown> = {}
			for (const key in row) {
				if (key !== 'id' && key !== '_id') {
					record[key] = row[key]
				}
			}
			return record
		})

		// Try to detect lat/lon columns
		const columns = await provider.getColumns()
		const latCol = columns.find(c =>
			c.id.toLowerCase().includes('lat') ||
			c.id.toLowerCase() === 'y'
		)
		const lonCol = columns.find(c =>
			c.id.toLowerCase().includes('lon') ||
			c.id.toLowerCase().includes('lng') ||
			c.id.toLowerCase() === 'x'
		)

		if (!latCol || !lonCol) {
			console.error('Could not detect latitude/longitude columns')
			return false
		}

		// Create new GeoPackage with a temporary name
		const { GeoPackageDatabase } = await import('@/earthbrowser/geopackage/GeoPackageDatabase')
		const tableName = outputName.replace(/[^a-zA-Z0-9_]/g, '_')
		const tempFilename = `export_${tableName}_${Date.now()}`
		const db = new GeoPackageDatabase(tempFilename, 'exports', false)

		// Initialize empty database
		await db.createNew()

		// Create table from data
		await db.createFromCSV({
			tableName,
			csvData,
			latColumn: latCol.id,
			lonColumn: lonCol.id,
			description,
			identifier: tableName
		})

		// Export to file
		const data = await db.exportFile()

		// Convert to regular ArrayBuffer for Blob
		const buffer = new Uint8Array(data).buffer

		// Trigger download
		const blob = new Blob([buffer], { type: 'application/geopackage+sqlite3' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `${tableName}.gpkg`
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		URL.revokeObjectURL(url)

		console.log(`Exported ${result.rows.length} rows to GeoPackage`)
		return true
	} catch (error) {
		console.error('Export to GeoPackage failed:', error)
		return false
	}
}

/**
 * Detect latitude/longitude columns from provider columns
 */
export async function detectGeometryColumns(provider: IDataProvider): Promise<{
	latColumn: string | null
	lonColumn: string | null
}> {
	const columns = await provider.getColumns()

	const latCol = columns.find(c =>
		c.id.toLowerCase().includes('lat') ||
		c.id.toLowerCase() === 'y'
	)
	const lonCol = columns.find(c =>
		c.id.toLowerCase().includes('lon') ||
		c.id.toLowerCase().includes('lng') ||
		c.id.toLowerCase() === 'x'
	)

	return {
		latColumn: latCol?.id ?? null,
		lonColumn: lonCol?.id ?? null
	}
}

/**
 * Export data from a provider to Shapefile format (ZIP)
 *
 * @param options Export configuration
 * @returns Promise resolving to true on success
 */
export async function exportToShapefile(options: ExportToGeoPackageOptions): Promise<boolean> {
	const {
		provider,
		outputName,
		maxRows = 100000
	} = options

	try {
		// Use the Shapefile export function
		const { exportToShapefile: createShapefileComponents } = await import('@/earthbrowser/formats/Shapefile')
		const result = await createShapefileComponents(provider, maxRows)

		if (!result) {
			return false
		}

		// Create a ZIP file containing all shapefile components
		// Note: Using manual ZIP creation to avoid adding jszip dependency
		const baseName = outputName.replace(/[^a-zA-Z0-9_]/g, '_')

		// Create a simple ZIP file manually
		const files = [
			{ name: `${baseName}.shp`, data: new Uint8Array(result.shp) },
			{ name: `${baseName}.shx`, data: new Uint8Array(result.shx) },
			{ name: `${baseName}.dbf`, data: new Uint8Array(result.dbf) },
			{ name: `${baseName}.prj`, data: new TextEncoder().encode(result.prj) }
		]

		const zipBlob = await createZipBlob(files)

		// Trigger download
		const url = URL.createObjectURL(zipBlob)
		const a = document.createElement('a')
		a.href = url
		a.download = `${baseName}.zip`
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		URL.revokeObjectURL(url)

		console.log(`Exported shapefile to ${baseName}.zip`)
		return true
	} catch (error) {
		console.error('Export to Shapefile failed:', error)
		return false
	}
}

/**
 * Calculate CRC-32 checksum for data
 */
function calculateCRC32(data: Uint8Array): number {
	// CRC-32 lookup table
	const table = new Uint32Array(256)
	for (let i = 0; i < 256; i++) {
		let c = i
		for (let j = 0; j < 8; j++) {
			c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1)
		}
		table[i] = c
	}

	let crc = 0xFFFFFFFF
	for (const byte of data) {
		crc = table[(crc ^ byte) & 0xFF] ^ (crc >>> 8)
	}

	return (crc ^ 0xFFFFFFFF) >>> 0
}

/**
 * Create a simple ZIP blob from files (minimal ZIP implementation)
 */
async function createZipBlob(files: Array<{ name: string; data: Uint8Array }>): Promise<Blob> {
	const encoder = new TextEncoder()
	const chunks: Uint8Array[] = []
	let offset = 0
	const centralDirectory: Uint8Array[] = []

	// Write each file
	for (const file of files) {
		const nameBytes = encoder.encode(file.name)
		const crc32 = calculateCRC32(file.data)

		const localHeaderBuffer = new ArrayBuffer(30 + nameBytes.length)
		const localHeader = new Uint8Array(localHeaderBuffer)
		const localHeaderView = new DataView(localHeaderBuffer)

		// Local file header
		localHeaderView.setUint32(0, 0x04034b50, true) // Local file header signature
		localHeaderView.setUint16(4, 20, true) // Version needed to extract
		localHeaderView.setUint16(6, 0, true) // General purpose bit flag
		localHeaderView.setUint16(8, 0, true) // Compression method (0 = stored)
		localHeaderView.setUint16(10, 0, true) // Last mod file time
		localHeaderView.setUint16(12, 0, true) // Last mod file date
		localHeaderView.setUint32(14, crc32, true) // CRC-32
		localHeaderView.setUint32(18, file.data.length, true) // Compressed size
		localHeaderView.setUint32(22, file.data.length, true) // Uncompressed size
		localHeaderView.setUint16(26, nameBytes.length, true) // File name length
		localHeaderView.setUint16(28, 0, true) // Extra field length

		localHeader.set(nameBytes, 30)
		chunks.push(localHeader)
		chunks.push(file.data)

		// Central directory entry
		const centralHeaderBuffer = new ArrayBuffer(46 + nameBytes.length)
		const centralHeader = new Uint8Array(centralHeaderBuffer)
		const centralHeaderView = new DataView(centralHeaderBuffer)

		centralHeaderView.setUint32(0, 0x02014b50, true) // Central file header signature
		centralHeaderView.setUint16(4, 20, true) // Version made by
		centralHeaderView.setUint16(6, 20, true) // Version needed to extract
		centralHeaderView.setUint16(8, 0, true) // General purpose bit flag
		centralHeaderView.setUint16(10, 0, true) // Compression method
		centralHeaderView.setUint16(12, 0, true) // Last mod file time
		centralHeaderView.setUint16(14, 0, true) // Last mod file date
		centralHeaderView.setUint32(16, crc32, true) // CRC-32
		centralHeaderView.setUint32(20, file.data.length, true) // Compressed size
		centralHeaderView.setUint32(24, file.data.length, true) // Uncompressed size
		centralHeaderView.setUint16(28, nameBytes.length, true) // File name length
		centralHeaderView.setUint16(30, 0, true) // Extra field length
		centralHeaderView.setUint16(32, 0, true) // File comment length
		centralHeaderView.setUint16(34, 0, true) // Disk number start
		centralHeaderView.setUint16(36, 0, true) // Internal file attributes
		centralHeaderView.setUint32(38, 0, true) // External file attributes
		centralHeaderView.setUint32(42, offset, true) // Relative offset of local header

		centralHeader.set(nameBytes, 46)
		centralDirectory.push(centralHeader)

		offset += localHeader.length + file.data.length
	}

	// End of central directory record
	const centralDirSize = centralDirectory.reduce((sum, cd) => sum + cd.length, 0)
	const endOfCentralDirBuffer = new ArrayBuffer(22)
	const endOfCentralDir = new Uint8Array(endOfCentralDirBuffer)
	const endView = new DataView(endOfCentralDirBuffer)

	endView.setUint32(0, 0x06054b50, true) // End of central dir signature
	endView.setUint16(4, 0, true) // Number of this disk
	endView.setUint16(6, 0, true) // Disk where central directory starts
	endView.setUint16(8, files.length, true) // Number of central directory records on this disk
	endView.setUint16(10, files.length, true) // Total number of central directory records
	endView.setUint32(12, centralDirSize, true) // Size of central directory
	endView.setUint32(16, offset, true) // Offset of start of central directory
	endView.setUint16(20, 0, true) // ZIP file comment length

	// Combine everything
	const allChunks = [...chunks, ...centralDirectory, endOfCentralDir] as BlobPart[]
	return new Blob(allChunks, { type: 'application/zip' })
}
